import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import "./styles/base.css";
import "./styles/rtl.css";
import { useSiteTranslation } from "./i18n";
import { useReveal } from "./hooks/useReveal";
import { HomeHeader } from "./sections/HomeHeader";
import { HomeFooter } from "./sections/HomeFooter";

const SITE_URL = "https://www.mjmarry.com";
// Paths that show another page's content send search engines to the original.
const CANONICAL_ALIASES = { "/about": "/" };

/** Adds a <link> or <meta> to <head> while the page is shown. */
function useHeadTag(tagName, attributes, enabled = true) {
  const serialized = JSON.stringify(attributes);
  useEffect(() => {
    if (!enabled) return undefined;
    const element = Object.assign(document.createElement(tagName), JSON.parse(serialized));
    document.head.appendChild(element);
    return () => element.remove();
  }, [tagName, serialized, enabled]);
}

/**
 * Page shell from the new design: announcement bar, header, footer and the
 * .mjm-home token scope. Used by the homepage and every public page.
 * `noindex` keeps a page (a 404 or a member's profile) out of search results.
 */
export function SiteLayout({ title, noindex = false, className = "", children }) {
  const rootRef = useRef(null);
  const { pathname, hash, key } = useLocation();
  const { t, language, dir } = useSiteTranslation();
  // Lists keyed by translated text remount when the language changes, so reveal them again.
  useReveal(rootRef, language);

  useEffect(() => {
    document.documentElement.classList.add("mjm-home-html");
    return () => document.documentElement.classList.remove("mjm-home-html");
  }, []);

  // The dashboard stays English, so only public pages change the document language.
  useEffect(() => {
    document.documentElement.lang = language;
    return () => {
      document.documentElement.lang = "en";
    };
  }, [language]);

  useEffect(() => {
    if (!title) return undefined;
    const previousTitle = document.title;
    document.title = title;
    return () => {
      document.title = previousTitle;
    };
  }, [title]);

  const path = pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;
  useHeadTag("link", { rel: "canonical", href: SITE_URL + (CANONICAL_ALIASES[path] ?? path) }, !noindex);
  useHeadTag("meta", { name: "robots", content: "noindex" }, noindex);

  // Route changes keep the old scroll position by default; jump to the hash target or the top instead.
  useEffect(() => {
    const target = hash ? document.getElementById(decodeURIComponent(hash.slice(1))) : null;
    if (target) target.scrollIntoView();
    else window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname, hash, key]);

  return (
    <div className={`mjm-home ${className}`.trim()} ref={rootRef} dir={dir} lang={language}>
      <a className="skip-link" href="#main">
        {t("common.skip")}
      </a>
      <HomeHeader />
      <main id="main">{children}</main>
      <HomeFooter />
    </div>
  );
}
